/* Copyright (c) 2022 chilled.dev */

import { Analytics, getAnalytics } from 'firebase/analytics';
import { FirebaseApp, initializeApp } from 'firebase/app';
import { Auth, getAuth } from 'firebase/auth';
import { Firestore, getFirestore } from 'firebase/firestore';
import { FirebasePerformance, getPerformance } from 'firebase/performance';
import {
  fetchAndActivate,
  getRemoteConfig,
  RemoteConfig,
} from 'firebase/remote-config';

import { createContext, useContext } from 'react';

export interface Firebase {
  app: FirebaseApp;
  auth: Auth;
  firestore: Firestore;
  analytics?: Analytics;
  performance?: FirebasePerformance;
  remoteConfig?: RemoteConfig;
}

const initializeFirebase = async (
  configPath = '/__/firebase/init.json',
): Promise<Firebase> => {
  const response = await fetch(configPath);
  const config = await response.json();

  const app = initializeApp(config);
  const auth = getAuth(app);
  const firestore = getFirestore(app);

  const firebase: Firebase = {
    app,
    auth,
    firestore,
  };

  // add analytics
  try {
    firebase.analytics = getAnalytics(app);
  } catch (error) {
    if ((error as any).code !== 'analytics/cookies-not-enabled') {
      console.error('could not initialize perfomance monitoring', error);
    }
  }

  // add performance
  try {
    firebase.performance = getPerformance(app);
  } catch (error) {
    console.error('could not initialize perfomance monitoring', error);
  }

  try {
    // add remote config
    const remoteConfig = getRemoteConfig(app);
    remoteConfig.settings.minimumFetchIntervalMillis =
      // set fetch interval to 1h on prod, to 1m on dev
      process.env.NODE_ENV === 'production' ? 3600000 : 60000;

    await fetchAndActivate(remoteConfig);

    firebase.remoteConfig = remoteConfig;
  } catch (error) {
    console.error('could not initialize perfomance monitoring', error);
  }

  return firebase;
};

const FirebaseContext = createContext<Firebase>({
  app: true as any,
  auth: true as any,
  firebase: true as any,
} as any);

const initFirebase = () => {
  let status = 'pending';
  let firebase: Firebase;
  let error: Error;
  let suspender = initializeFirebase().then(
    (r) => {
      status = 'success';
      firebase = r;
    },
    (e) => {
      status = 'error';
      error = e;
    },
  );
  return {
    read() {
      if (status === 'pending') {
        throw suspender;
      } else if (status === 'error') {
        throw error;
      } else if (status === 'success') {
        return firebase;
      }
    },
  };
};

const resource = initFirebase();

const FirebaseProvider = ({ children }: { children: React.ReactNode }) => {
  const value = resource.read();

  return (
    <FirebaseContext.Provider value={value as any}>
      {value && children}
    </FirebaseContext.Provider>
  );
};

const useFirebase = (): Firebase => useContext(FirebaseContext);

export { FirebaseProvider, FirebaseContext, useFirebase };
